<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { getNotificationsContext } from '@client/components/notifications';

  import { getWorkflowActionByValue } from '@client/pages/staff/workflows/workflow-steps-config';

  import { appStore } from '@client/stores/app';
  import { cloneWorkflow, deleteWorkflow, exportWorkflow } from '@client/stores/workflows';

  import { downloadFile } from '@client/utils/download';

  import { generateUUID } from '@common/utils/strings';

  const { addNotification } = getNotificationsContext();

  export let workflow = {};
  export let refetch = () => {};
  export let onEdit = () => {};

  let showMenu = false;
  let dropdownRef = null;
  let active = false;

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  const notify = (text, type = 'success') => {
    addNotification({
      text,
      type,
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onClick = (e) => {
    const element = e.target;

    active = true;

    if (dropdownRef === element || dropdownRef?.contains(element)) {
      active = false;
    } else {
      onRun();
    }
  };

  const onMouseUp = () => {
    active = false;
  };

  const onDelete = async () => {
    const confirmed = confirm($format('label.DELETE_PROMPT', { values: { type: 'workflow' } }));

    if (confirmed) {
      $appStore.loading = true;

      try {
        await deleteWorkflow(workflow.name);
        notify($format('label.SUCCESS.DELETE', { values: { type: workflow.name } }));
        refetch();
      } catch (error) {
        notify(error.message || 'Failed to delete workflow', 'error');
      } finally {
        $appStore.loading = false;
      }
    }
  };

  const onClone = async () => {
    $appStore.loading = true;

    try {
      const clonedWorkflow = await cloneWorkflow(workflow.name);
      notify(`Workflow "${workflow.name}" cloned successfully`);
      refetch();
    } catch (error) {
      notify(error.message || 'Failed to clone workflow', 'error');
    } finally {
      $appStore.loading = false;
    }
  };

  const onRun = () => {
    showMenu = false;
    navigate(`/staff/workflows/${workflow.name}`);
  };

  const handleEdit = () => {
    showMenu = false;
    onEdit(workflow);
  };

  const onExport = () => {
    showMenu = false;

    try {
      const workflowData = exportWorkflow(workflow.name);
      const json = JSON.stringify(workflowData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const filename = `${workflow.name.replace(/\s+/g, '_')}_workflow.json`;

      downloadFile(blob, filename);
      notify(`Workflow "${workflow.name}" exported successfully`);
    } catch (error) {
      notify(error.message || 'Failed to export workflow', 'error');
    }
  };

  $: stepCount = workflow.steps?.length || 0;
  $: tooltipId = `workflow-steps-${generateUUID()}`;
  $: stepsList =
    workflow.steps?.map((step, index) => {
      const actionValue = typeof step.action === 'string' ? step.action : step.action?.value;
      const action = getWorkflowActionByValue(actionValue);
      return {
        number: index + 1,
        label: action ? `${action.categoryLabel} > ${action.itemLabel} > ${action.label}` : 'Unknown action'
      };
    }) || [];
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="workflow-card"
  class:active
  on:click={onClick}
  on:mouseleave={() => (showMenu = false)}
  on:mouseup={onMouseUp}
  role="button"
  tabindex="0"
>
  <div bind:this={dropdownRef} class="action" on:click|stopPropagation role="button" tabindex="-1">
    <Dropdown isOpen={showMenu} toggle={() => (showMenu = !showMenu)}>
      <DropdownToggle tag="div" class="os-icon-more-horizontal" />
      <DropdownMenu>
        <DropdownItem on:click={onRun}>Run</DropdownItem>
        <DropdownItem divider />
        <DropdownItem on:click={handleEdit}>{$format('label.EDIT')}</DropdownItem>
        <DropdownItem on:click={onClone}>{$format('label.CLONE')}</DropdownItem>
        <DropdownItem divider />
        <DropdownItem on:click={onExport}>Export Workflow</DropdownItem>
        <DropdownItem divider />
        <DropdownItem on:click={onDelete}>{$format('label.DELETE')}</DropdownItem>
      </DropdownMenu>
    </Dropdown>
  </div>

  <div class="workflow">
    <div class="section name">
      <small>WORKFLOW</small>
      <div class="workflow-name">{workflow.name}</div>
    </div>

    <div class="section steps">
      <small>STEPS</small>
      <div class="step-count" id={tooltipId}>
        {stepCount}
        {#if stepCount === 1}
          step
        {:else}
          steps
        {/if}
      </div>

      {#if stepCount > 0}
        <Tooltip target={tooltipId} placement="bottom">
          {#each stepsList as step, i}
            {step.number}. {step.label}
            {#if i < stepsList.length - 1}<br /><br />{/if}
          {/each}
        </Tooltip>
      {/if}
    </div>
  </div>
</div>

<style lang="scss" src="./workflow-card.scss"></style>
